// JournalArticleForm migration: 1.2 Article — TypeScript record schema (article/journal/issue)
import {
  abstractDefinition,
  contributorDefinition,
  contributorsDefinition,
  doiDefinition,
  issnDefinition,
  landingPageUrlDefinition,
  languageCodesDefinition,
  referencesDefinition,
  similarityCheckUrlDefinition,
} from '../common/common-schemas';

// JournalArticleForm migration: 1.2 migrate fields
const commonDefinitions = {
  abstractItem: abstractDefinition,
  contributor: contributorDefinition,
  // JournalArticleForm migration: 1.2 Contributors
  contributors: contributorsDefinition,
  crossref_language_codes: languageCodesDefinition,
  doi: doiDefinition,
  issn: issnDefinition,
  landingPageUrl: landingPageUrlDefinition,
  // JournalArticleForm migration: 1.2 Abstract
  abstract: {
    type: 'array',
    default: [{}],
    items: {
      $ref: '#/definitions/abstractItem',
    },
    i18n: 'abstracts',
  },
  // JournalArticleForm migration: 1.2 Publication Dates
  publicationDates: {
    type: 'object',
    i18n: 'contentRegistrationForm_article_publicationDates',
    default: {},
    requireAny: ['publishedOnline', 'publishedInPrint'],
    properties: {
      publishedOnline: {
        type: 'string',
        format: 'date',
      },
      publishedInPrint: {
        type: 'string',
        format: 'date',
      },
    },
  },
  // JournalArticleForm migration: 1.2 Page or Article Number
  pageOrArticleNumber: {
    type: 'object',
    i18n: 'contentRegistrationForm_article_pageOrArticleNumber',
    properties: {
      firstPageNumber: {
        type: 'string',
        pattern: '^[0-9a-zA-Z]+$',
        i18n: 'contentRegistrationForm_article_pageOrArticleNumber_firstPageNumber',
      },
      articleNumberOrId: {
        type: 'string',
        i18n: 'contentRegistrationForm_article_pageOrArticleNumber_articleNumberOrId',
      },
    },
  },
  // JournalArticleForm migration: 1.2 Funding
  fundingItem: {
    title: 'Funding',
    type: 'object',
    required: [],
    dependencies: {
      awardNumber: ['funder-name'],
    },
    properties: {
      'funder-name': {
        i18n: 'contentRegistrationForm_journalArticle_funding_funderName',
        type: 'string',
        'x-rorIdField': 'funderRorId',
      },
      funderRorId: {
        i18n: 'contentRegistrationForm_journalArticle_funding_funderRorId',
        type: 'string',
        pattern: '^https://ror.org/*',
      },
      awardNumber: {
        type: 'string',
        i18n: 'contentRegistrationForm_journalArticle_funding_awardNumber',
      },
    },
  },
  funding: {
    type: 'array',
    default: [],
    items: {
      $ref: '#/definitions/fundingItem',
    },
    i18n: 'contentRegistrationForm_article_funding',
  },
  // JournalArticleForm migration: 1.2 Relationships
  relationshipItem: {
    type: 'object',
    properties: {
      relationType: {
        type: 'string',
        enum: [
          'hasTranslation',
          'isTranslationOf',
          'hasPreprint',
          'hasVersion',
          'isVersionOf',
          'isSupplementTo',
          'isSupplementedBy',
          'isIdenticalTo',
          'hasReview',
          'isContinuedBy',
          'Continues',
        ],
        i18n: 'contentRegistrationForm_article_relationship_type',
      },
      relatedDoi: {
        ...doiDefinition,
        i18n: 'contentRegistrationForm_article_relationship_doi',
      },
    },
    dependencies: {
      relationType: ['relatedDoi'],
      relatedDoi: ['relationType'],
    },
  },
  relationships: {
    type: 'array',
    default: [],
    items: {
      $ref: '#/definitions/relationshipItem',
    },
    i18n: 'contentRegistrationForm_article_relationships',
  },
  // JournalArticleForm migration: 1.2 License
  license: {
    type: 'string',
    format: 'url',
    i18n: 'contentRegistrationForm_article_license',
  },
  // JournalArticleForm migration: 1.2 Similarity check URL
  simCheckUrl: {
    ...similarityCheckUrlDefinition,
    i18n: 'contentRegistrationForm_article_simCheckUrl',
  },
  // JournalArticleForm migration: 1.2 References
  references: {
    ...referencesDefinition,
    i18n: 'contentRegistrationForm_article_references',
  },
};

// JournalArticleForm migration: 1.3 migrate fields
export const journalArticleJournalSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Journal Article Journal',
  type: 'object',
  properties: {
    titles: {
      type: 'object',
      properties: {
        // JournalArticleForm migration: 1.3 Full title
        fullTitle: {
          type: 'string',
          i18n: 'records_editForm_journal_fullTitle',
        },
        // JournalArticleForm migration: 1.3 Abbreviated title
        abbreviatedTitle: {
          type: 'string',
          i18n: 'records_editForm_journal_abbreviatedTitle',
        },
      },
      required: ['fullTitle'],
    },
    issn: {
      type: 'object',
      properties: {
        // JournalArticleForm migration: 1.3 Print ISSN of journal
        printIssn: {
          $ref: '#/definitions/issn',
        },
        // JournalArticleForm migration: 1.3 Online ISSN of journal
        onlineIssn: {
          $ref: '#/definitions/issn',
        },
      },
    },
    // JournalArticleForm migration: 1.3 Crossmark policy page
    crossmarkPolicyPage: {
      ...doiDefinition,
      i18n: 'form_input_crossmark_policy_page',
    },
    // JournalArticleForm migration: 1.3 Journal DOI
    doi: {
      ...doiDefinition,
      i18n: 'form_input_doi',
    },
    // JournalArticleForm migration: 1.3 Journal resource URL
    resourceUrl: {
      type: 'string',
      format: 'url',
      notDoiUrl: true,
      i18n: 'form_input_resource_url',
    },
  },
  allOf: [
    {
      if: {
        not: {
          required: ['issn'],
        },
      },
      then: {
        required: ['doi'],
      },
    },
    {
      if: {
        required: ['doi'],
      },
      then: {
        required: ['resourceUrl'],
      },
    },
  ],
  required: ['issn'],
  definitions: {
    issn: issnDefinition,
    doi: doiDefinition,
  },
};

// JournalArticleForm migration: 1.4 Issue metadata
export const journalArticleIssueSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Journal Article Issue',
  type: 'object',
  properties: {
    // JournalArticleForm migration: 1.4 Published online
    publishedOnline: {
      type: 'string',
      format: 'date',
      i18n: 'form_input_issue_published_online',
    },
    // JournalArticleForm migration: 1.4 Published in print
    publishedInPrint: {
      type: 'string',
      format: 'date',
      i18n: 'form_input_issue_published_in_print',
    },
    // JournalArticleForm migration: 1.4 Issue number
    issue: {
      type: 'string',
      i18n: 'records_editForm_issue_issueNumber',
    },
    // JournalArticleForm migration: 1.4 Volume
    volume: {
      type: 'string',
      maxLength: 32,
      i18n: 'form_input_issue_volume',
    },
  },
};

// JournalArticleForm migration: 1.3 Journal step
export const journalArticleJournalStepSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Journal Article Journal Step',
  type: 'object',
  properties: {
    journal: journalArticleJournalSchema,
  },
  definitions: journalArticleJournalSchema.definitions,
};

export const journalArticleSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Journal Article Article',
  type: 'object',
  mustDiffer: ['landingPageUrl', 'license'],
  required: ['title', 'doi', 'landingPageUrl', 'contributors', 'publicationDates'],
  properties: {
    title: {
      type: 'string',
      i18n: 'form_input_article_title_title',
    },
    doi: {
      ...doiDefinition,
      i18n: 'contentRegistrationForm_article_doi',
    },
    landingPageUrl: {
      ...landingPageUrlDefinition,
      i18n: 'contentRegistrationForm_article_resourceUrl',
    },
    contributors: {
      $ref: '#/definitions/contributors',
    },
    abstract: {
      $ref: '#/definitions/abstract',
    },
    publicationDates: {
      $ref: '#/definitions/publicationDates',
    },
    pageOrArticleNumber: {
      $ref: '#/definitions/pageOrArticleNumber',
    },
    funding: {
      $ref: '#/definitions/funding',
    },
    license: {
      $ref: '#/definitions/license',
    },
    simCheckUrl: {
      $ref: '#/definitions/simCheckUrl',
    },
    relationships: {
      $ref: '#/definitions/relationships',
    },
    references: {
      $ref: '#/definitions/references',
    },
  },
  definitions: {
    ...commonDefinitions,
  },
};

export const journalArticleRecordSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Journal Article',
  type: 'object',
  required: ['article', 'journal'],
  properties: {
    article: journalArticleSchema,
    // JournalArticleForm migration: 1.3 Journal step
    journal: journalArticleJournalSchema,
    // JournalArticleForm migration: 1.4 Issue metadata
    issue: journalArticleIssueSchema,
  },
  definitions: {
    ...commonDefinitions,
  },
};
